import type { VariantProps } from "@-/com.npmjs.cva";
import { Icon } from "@-/dev.improve.haniverse.components.ui.icon";
import { TextClassContext } from "@-/dev.improve.haniverse.components.ui.text";
import { toggleVariants } from "@-/dev.improve.haniverse.components.ui.toggle";
import { cn } from "@-/com.npmjs.cn";
import {
  Item,
  type ItemProps,
  type ItemRef,
  Root,
  type RootProps,
  type RootRef,
  useRootContext,
  utils,
} from "@rn-primitives/toggle-group";
import { defaultError } from "@-/com.npmjs.react-context-default-error";
import { type ComponentProps, createContext, type RefAttributes, use } from "react";
import { Platform } from "@-/com.npmjs.react-native";

const ToggleGroupContext = createContext(
  defaultError<VariantProps<typeof toggleVariants>>("ToggleGroupContext"),
);

export function ToggleGroup({
  children,
  className,
  size,
  variant,
  ...props
}: RootProps & VariantProps<typeof toggleVariants> & RefAttributes<RootRef>) {
  return (
    <Root
      className={cn(
        "flex flex-row items-center rounded-xs shadow-none",
        Platform.select({ web: "w-fit" }),
        variant === "outline" && "shadow-sm shadow-black/5",
        className,
      )}
      {...props}
    >
      <ToggleGroupContext value={{ size, variant }}>{children}</ToggleGroupContext>
    </Root>
  );
}

function useToggleGroupContext() {
  return use(ToggleGroupContext);
}

export function ToggleGroupItem({
  children,
  className,
  isFirst,
  isLast,
  size,
  variant,
  ...props
}: ItemProps &
  VariantProps<typeof toggleVariants> &
  RefAttributes<ItemRef> & {
    isFirst?: boolean;
    isLast?: boolean;
  }) {
  const context = useToggleGroupContext();
  const { value } = useRootContext();

  return (
    <TextClassContext
      value={cn(
        "text-sm font-medium text-foreground",
        utils.getIsSelected(value, props.value)
          ? "text-accent-foreground"
          : Platform.select({ web: "group-hover:text-muted-foreground" }),
      )}
    >
      <Item
        className={cn(
          toggleVariants({
            size: context.size || size,
            variant: context.variant || variant,
          }),
          props.disabled && "opacity-50",
          utils.getIsSelected(value, props.value) && "bg-accent",
          "min-w-0 shrink-0 rounded-none shadow-none",
          isFirst && "rounded-l-xs",
          isLast && "rounded-r-xs",
          (context.variant === "outline" || variant === "outline") && "border-l-0",
          (context.variant === "outline" || variant === "outline") && isFirst && "border-l",
          Platform.select({
            web: "flex-1 focus:z-10 focus-visible:z-10",
          }),
          className,
        )}
        {...props}
      >
        {children}
      </Item>
    </TextClassContext>
  );
}

export function ToggleGroupIcon({ className, ...props }: ComponentProps<typeof Icon>) {
  const textClass = use(TextClassContext);
  return <Icon className={cn("size-4 shrink-0", textClass, className)} size={16} {...props} />;
}
